import { Button, Flex, Form, Input, message } from "antd";
import styled from "styled-components";
import { useLocation, useNavigate } from "react-router-dom";
import PageLayout from "layouts/login/pageLayout";
import { useSendMfaTokenMutation, useVerifyMfaCodeMutation } from "features/auth/authAPI";
import { useCallback, useEffect, useRef, useState } from "react";

const StyledForm = styled(Form)`
  width: 350px;
  margin: 0 auto;
  padding: 20px;
`;

const StyledButton = styled(Button)`
  width: 100%;
  height: 50px;
`;

const PickerContainer = styled.div`
  width: 350px;
  margin: 0 auto;
  padding: 20px;
  text-align: center;
`;

const OTPContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 8px;
  margin: 16px 0;
`;

const StyledInput = styled(Input)`
  width: 32px;
  height: 40px;
  text-align: center;
`;

const ClearLink = styled(Button)`
  padding: 0;
  height: auto;
  color: #1890ff;
  &:hover {
    color: #40a9ff;
    background: transparent;
  }
`;

function OtpFormPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const { requiredMfaDevices } = location.state || { requiredMfaDevices: null };
  const [verifyMfaCode] = useVerifyMfaCodeMutation();
  const [sendMfaToken, { isLoading: isSendingMfaToken }] = useSendMfaTokenMutation();
  const [form] = Form.useForm();
  const hasMultipleDevices = (requiredMfaDevices?.length ?? 0) > 1;
  const [selectedDevice, setSelectedDevice] = useState(null);
  const hasAutoPickedRef = useRef(false);

  const onPickDevice = useCallback(
    async (device) => {
      // TOTP codes are read directly from the user's authenticator app -- no
      // server-side challenge needed. Transition immediately.
      if (device.deviceType === "totp") {
        setSelectedDevice(device);
        form.resetFields();
        return;
      }
      // For side-channel devices (currently only email; SMS / phone in the
      // future), wait for the send to succeed before transitioning. The global
      // RTK error middleware surfaces failures via toast; on failure we stay
      // on the picker so the user can retry instead of being stranded on an
      // OTP form expecting a code that never arrived.
      const result = await sendMfaToken({ persistentId: device.persistentId });
      if ("data" in result) {
        setSelectedDevice(device);
        form.resetFields();
        message.success("A verification code has been sent to your email.");
      }
    },
    [form, sendMfaToken],
  );

  // Single-device users skip the picker, but they still need the same
  // send-token-on-pick semantics so the backend issues a code. Auto-pick on
  // mount routes through onPickDevice. The ref guards against React strict
  // mode running effects twice in development (which would double-send).
  useEffect(() => {
    if (!selectedDevice && requiredMfaDevices?.length === 1 && !hasAutoPickedRef.current) {
      hasAutoPickedRef.current = true;
      onPickDevice(requiredMfaDevices[0]);
    }
  }, [requiredMfaDevices, selectedDevice, onPickDevice]);

  const onUseDifferentMethod = () => {
    setSelectedDevice(null);
    form.resetFields();
  };

  const onKeyUp = (e, index) => {
    if (e.key === "Backspace" && !e.target.value && index > 0) {
      e.target.previousElementSibling?.focus();
    }
  };

  const onFinish = async (values) => {
    const result = await verifyMfaCode({
      token: values.code.join(""),
      persistentId: selectedDevice.persistentId,
    });

    if ("data" in result) {
      navigate("/accounts");
    }
  };

  const handleInput = (e) => {
    const input = e.target;
    const { value } = input;
    // Only allow single digit numbers
    if (/^\d$/.test(value)) {
      // Move to next input if available
      if (input.nextElementSibling && input.nextElementSibling.nextElementSibling) {
        input.nextElementSibling.focus();
      }
    } else {
      input.value = "";
    }
  };

  const handlePaste = (e) => {
    e.preventDefault();
    const paste = e.clipboardData.getData("text").trim();
    // Only allow numbers in paste
    const hasExtraChars = paste.match(/[^0-9]/g);
    if (!hasExtraChars) {
      const digits = paste.split("").slice(0, 6);
      if (digits.length < 6) {
        digits.push(...Array(6 - digits.length).fill(""));
      }
      form.setFieldsValue({
        code: digits,
      });
      // Focus the last input
      const inputs = Array.from(e.target.closest("form").querySelectorAll("input"));
      inputs[paste.length]?.focus();
    }
  };

  const handleClear = () => {
    form.setFieldsValue({
      code: Array(6).fill(""),
    });
    // Focus the first input after clearing
    const inputs = document.querySelectorAll("input");
    inputs[0]?.focus();
  };

  if (!selectedDevice) {
    return (
      <PageLayout>
        <PickerContainer>
          <h3>Two-factor Authentication</h3>
          <p>Choose how you want to verify your identity:</p>
          <Flex vertical gap={12}>
            {requiredMfaDevices?.map((device) => (
              <StyledButton
                key={device.persistentId}
                type="primary"
                loading={isSendingMfaToken}
                onClick={() => onPickDevice(device)}
              >
                {device.deviceType === "totp" ? "Use authenticator app" : "Email me a code"}
              </StyledButton>
            ))}
          </Flex>
        </PickerContainer>
      </PageLayout>
    );
  }

  return (
    <PageLayout>
      <StyledForm form={form} onFinish={onFinish}>
        <h3>Two-factor Authentication</h3>
        <p>
          {selectedDevice.deviceType === "totp"
            ? `Enter the 6-digit code from your authenticator app`
            : "A 6-digit code was sent to your email. Please enter it below:"}
        </p>
        <Form.List name="code" initialValue={Array(6).fill("")}>
          {(fields) => (
            <OTPContainer>
              {fields.map((field, index) => (
                <Form.Item
                  {...field}
                  key={field.key}
                  noStyle
                  rules={[{ required: true, message: "" }]}
                >
                  <StyledInput
                    maxLength={1}
                    onKeyUp={(e) => onKeyUp(e, index)}
                    onPaste={handlePaste}
                    onInput={handleInput}
                    inputMode="numeric"
                    pattern="[0-9]*"
                  />
                </Form.Item>
              ))}
              <ClearLink type="link" onClick={handleClear}>
                Clear
              </ClearLink>
            </OTPContainer>
          )}
        </Form.List>
        <Form.Item>
          <StyledButton type="primary" htmlType="submit">
            Verify
          </StyledButton>
        </Form.Item>
        {hasMultipleDevices && (
          <Flex justify="center">
            <Button type="link" onClick={onUseDifferentMethod}>
              Use a different method
            </Button>
          </Flex>
        )}
      </StyledForm>
    </PageLayout>
  );
}

export default OtpFormPage;
